import * as B from '@blueprintjs/core';
import {DateRange, DateRangePicker3} from '@blueprintjs/datetime2';
import moment from 'moment';
import React from 'react';

import {OptionCard} from 'app/components/AgentExplorer/OptionCard';
import {AgentSceneMode} from 'app/components/AgentExplorer/types';

import cs from './styles.module.scss';
import {ResolutionChoice, RunPlanDraft} from './types';

const SCENE_MODE_OPTIONS: {
  value: AgentSceneMode;
  icon: B.IconName;
  label: string;
  description: string;
}[] = [
  {
    value: 'compare',
    icon: 'comparison',
    label: 'Compare two images',
    description: 'The agent reviews two dates per property and reports what changed.',
  },
  {
    value: 'single',
    icon: 'media',
    label: 'View a single image',
    description: 'The agent reviews one date per property and reports what it sees.',
  },
];

//
const RESOLUTION_OPTIONS: {value: ResolutionChoice; label: string}[] = [
  {value: 'any', label: 'Any resolution'},
  {value: '1', label: '1m or finer'},
  {value: '1.5', label: '1.5m or finer'},
  {value: '5', label: '5m or finer'},
  {value: 'custom', label: 'Custom'},
];

const formatRange = (range: DateRange) =>
  range[0] && range[1]
    ? `${moment(range[0]).format('LL')} - ${moment(range[1]).format('LL')}`
    : 'Select dates';

const DateWindow: React.FC<{
  label: string;
  value: DateRange;
  onChange: (range: DateRange) => void;
}> = ({label, value, onChange}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <B.FormGroup label={label} className={cs.dateWindow}>
      <B.Popover
        fill
        minimal
        placement="bottom-start"
        isOpen={isOpen}
        onInteraction={setIsOpen}
        content={
          <DateRangePicker3
            value={value}
            maxDate={new Date()}
            onChange={(range) => {
              onChange(range);
              // A shortcut fills both ends at once, and so does the second click of a manual
              // pick: either way the window is settled and the calendar has nothing left to ask.
              if (range[0] && range[1]) {
                setIsOpen(false);
              }
            }}
          />
        }
      >
        <B.Button
          icon="calendar"
          rightIcon="caret-down"
          text={formatRange(value)}
          alignText="left"
          fill
        />
      </B.Popover>
    </B.FormGroup>
  );
};

export const ImageryStep: React.FC<{
  draft: RunPlanDraft;
  setDraft: (patch: Partial<RunPlanDraft>) => void;
}> = ({draft, setDraft}) => {
  const compareMode = draft.sceneMode === 'compare';
  const usesDateRanges = draft.imageryWindow === 'dateRange';

  return (
    <div className={cs.step}>
      <B.FormGroup label="Select the imagery that the Lens agent will review.">
        <div className={cs.optionGrid}>
          {SCENE_MODE_OPTIONS.map((option) => (
            <OptionCard
              key={option.value}
              icon={option.icon}
              title={option.label}
              description={option.description}
              selected={draft.sceneMode === option.value}
              // Prompts are worded per scene mode, so a preset picked for the other mode no
              // longer applies.
              onSelect={() => setDraft({sceneMode: option.value, promptKey: null})}
            />
          ))}
        </div>
      </B.FormGroup>
      <B.FormGroup label="What dates should the agent read imagery from?">
        <div className={cs.optionGrid}>
          <OptionCard
            icon="history"
            title={compareMode ? 'The two most recent' : 'The most recent'}
            description="Scenes are resolved per property when the run starts."
            selected={draft.imageryWindow === 'latest'}
            onSelect={() => setDraft({imageryWindow: 'latest'})}
          />
          <OptionCard
            icon="calendar"
            title="A date range"
            description={
              compareMode
                ? 'Read a scene from each window you pick.'
                : 'Read a scene from a window you pick.'
            }
            selected={draft.imageryWindow === 'dateRange'}
            onSelect={() => setDraft({imageryWindow: 'dateRange'})}
          />
        </div>
      </B.FormGroup>
      {usesDateRanges && (
        <div className={cs.dateWindowRow}>
          {compareMode && (
            <DateWindow
              label="Earlier image from"
              value={draft.beforeDateRange}
              onChange={(beforeDateRange) => setDraft({beforeDateRange})}
            />
          )}
          <DateWindow
            label={compareMode ? 'Later image from' : 'Image from'}
            value={draft.afterDateRange}
            onChange={(afterDateRange) => setDraft({afterDateRange})}
          />
        </div>
      )}
      <B.FormGroup label="What imagery resolution can be used?">
        <div className={cs.resolutionRow}>
          <B.RadioGroup
            inline
            selectedValue={draft.resolutionChoice}
            onChange={(event) =>
              setDraft({resolutionChoice: event.currentTarget.value as ResolutionChoice})
            }
          >
            {RESOLUTION_OPTIONS.map(({value, label}) => (
              <B.Radio key={value} value={value} label={label} />
            ))}
          </B.RadioGroup>
          {draft.resolutionChoice === 'custom' && (
            <>
              Enter a max imagery resolution in meters (e.g., 0.5m) for the agent to select. The
              smaller the number, the more detailed the imagery. If no imagery is available at that
              resolution for that property, that property will be skipped.
              <B.NumericInput
                className={cs.customResolutionInput}
                aria-label="Minimum resolution in meters"
                placeholder="Meters"
                min={0}
                stepSize={0.5}
                minorStepSize={0.1}
                value={draft.customResolution}
                onValueChange={(_value, valueAsString) =>
                  setDraft({customResolution: valueAsString})
                }
                rightElement={<B.Tag minimal>m</B.Tag>}
              />
            </>
          )}
        </div>
      </B.FormGroup>
    </div>
  );
};
